import { Link, useParams } from "react-router-dom";
import VideoCard from "@/components/base/VideoCard";
import { useSeo } from "@/hooks/useSeo";
import { getSpecialById, getVideosByIds } from "@/utils/video";
import { specials } from "@/mocks/specials";

export default function Special() {
  const { specialId = "" } = useParams();
  const special = getSpecialById(specialId);

  useSeo({
    title: special ? `${special.title} - 糖心Vlog精选专题` : "精选专题 - 糖心Vlog",
    description: special
      ? `${special.intro} 糖心Vlog 专题合集，共 ${special.videoIds.length} 个视频。`
      : "糖心Vlog 精选专题合集。",
    keywords: special
      ? `${special.title},糖心Vlog专题,视频合集,真实生活`
      : "糖心Vlog,专题合集",
    path: `/special/${specialId}`,
  });

  if (!special) {
    return (
      <div className="mx-auto flex max-w-7xl flex-col items-center px-4 py-24 text-center md:px-8">
        <i className="ri-bookmark-line text-5xl text-foreground-400" />
        <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">专题不存在</h1>
        <p className="mt-2 text-sm text-foreground-600">没有找到对应的专题合集。</p>
        <Link
          to="/"
          className="mt-6 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const list = getVideosByIds(special.videoIds);
  const others = specials.filter((s) => s.id !== special.id);

  return (
    <div>
      {/* banner */}
      <section className="relative h-[320px] w-full overflow-hidden md:h-[420px]">
        <img
          src={special.cover}
          alt={`${special.title} 专题封面`}
          title={`${special.title} - 糖心Vlog 专题`}
          className="absolute inset-0 h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/45 to-foreground-950/20" />
        <div className="relative mx-auto flex h-full max-w-7xl flex-col justify-end px-4 pb-8 md:px-8 md:pb-12">
          <nav className="absolute top-6 flex flex-wrap items-center gap-2 text-sm text-background-100/90 md:left-8">
            <Link to="/" className="transition-colors hover:text-background-50">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span>精选专题</span>
          </nav>
          <span className="inline-flex w-fit items-center gap-1 rounded-md bg-accent-500 px-2 py-0.5 text-xs font-medium text-background-50">
            <i className="ri-bookmark-3-fill" /> 专题合集
          </span>
          <h1 className="mt-3 font-heading text-3xl font-extrabold text-background-50 md:text-5xl">
            {special.title}
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-background-100/90 md:text-base">
            {special.intro}
          </p>
          <p className="mt-3 text-xs text-background-100/80">
            共 {special.videoIds.length} 个视频 · 由糖心Vlog 精心整理
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-10 md:px-8 md:py-14">
        {/* reason */}
        <div className="flex items-start gap-4 rounded-lg border border-primary-200 bg-primary-50 p-5 md:p-6">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-500 text-xl text-background-50">
            <i className="ri-sparkling-2-line" />
          </span>
          <div>
            <h2 className="font-heading text-base font-bold text-foreground-950">推荐理由</h2>
            <p className="mt-1.5 text-sm leading-relaxed text-foreground-700">{special.reason}</p>
          </div>
        </div>

        {/* videos */}
        <div className="mt-10">
          <h2 className="mb-6 font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
            专题内视频
          </h2>
          {list.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-20 text-center">
              <i className="ri-inbox-line text-5xl text-foreground-400" />
              <p className="mt-4 text-sm text-foreground-600">该专题暂无内容，敬请期待</p>
              <Link
                to="/"
                className="mt-6 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                返回首页
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {list.map((v, i) => (
                <VideoCard key={v.id} video={v} index={i} />
              ))}
            </div>
          )}
        </div>

        {/* related specials */}
        <div className="mt-14">
          <h2 className="mb-6 font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
            相关专题
          </h2>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {others.map((s) => (
              <Link
                key={s.id}
                to={`/special/${s.id}`}
                className="group flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-all duration-300 hover:-translate-y-0.5 hover:border-primary-300"
              >
                <div className="relative h-20 w-32 shrink-0 overflow-hidden rounded-md bg-background-200">
                  <img
                    src={s.cover}
                    alt={s.title}
                    title={`${s.title} - 糖心Vlog 专题`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="min-w-0">
                  <h3 className="font-heading text-base font-bold text-foreground-950 transition-colors group-hover:text-primary-600">
                    {s.title}
                  </h3>
                  <p className="mt-1 line-clamp-2 text-sm text-foreground-600">{s.intro}</p>
                  <p className="mt-1 text-xs text-foreground-500">{s.videoIds.length} 个视频</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}